import { useState, useEffect } from 'react';
import axios from 'axios';
import { ClientLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  Users, Plus, Trash2, Loader2, Mail, KeyRound,
  Shield, FileText, Eye, EyeOff, Pencil, Save, X
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamsPage() {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', password: '' });
  const [adding, setAdding] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [accessMember, setAccessMember] = useState(null);
  const [accessSaving, setAccessSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [membersRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/team/members`),
        axios.get(`${API_URL}/submissions`)
      ]);
      setMembers(membersRes.data);
      setSubmissions(subsRes.data);
    } catch (err) {
      toast.error('Failed to load team data');
    } finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (addForm.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setAdding(true);
    try {
      await axios.post(`${API_URL}/team/members`, addForm);
      toast.success(`Team member ${addForm.name} added`);
      setShowAdd(false);
      setAddForm({ email: '', name: '', password: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add team member');
    } finally { setAdding(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/team/members/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} removed`);
      loadData();
    } catch (err) {
      toast.error('Failed to remove team member');
    } finally { setDeleting(false); setDeleteTarget(null); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      await axios.put(`${API_URL}/team/members/${editTarget.id}`, editForm);
      toast.success('Team member updated');
      setEditTarget(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setEditSaving(false); }
  };

  const toggleAccess = async (memberId, submissionId, currentAccess) => {
    const member = members.find(m => m.id === memberId);
    if (!member) return;
    const currentIds = member.submission_access || [];
    const newIds = currentAccess
      ? currentIds.filter(id => id !== submissionId)
      : [...currentIds, submissionId];

    setAccessSaving(true);
    try {
      await axios.put(`${API_URL}/team/members/${memberId}/access`, { submission_ids: newIds });
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, submission_access: newIds } : m));
      toast.success('Access updated');
    } catch (err) {
      toast.error('Failed to update access');
    } finally { setAccessSaving(false); }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Team Members
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Add team members and assign them access to specific documents.
            </p>
          </div>
          <Button onClick={() => setShowAdd(true)} className="btn-primary gap-2" data-testid="add-team-member-btn">
            <Plus className="h-4 w-4" /> Add Member
          </Button>
        </div>

        {/* Add Member Form */}
        {showAdd && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="add-member-form">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#FF6B35]" /> New Team Member
            </h2>
            <form onSubmit={handleAdd} className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Full Name</Label>
                <Input value={addForm.name} onChange={e => setAddForm(f => ({...f, name: e.target.value}))} placeholder="John Doe" required data-testid="team-add-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({...f, email: e.target.value}))} placeholder="john@company.com" required data-testid="team-add-email" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={addForm.password}
                    onChange={e => setAddForm(f => ({...f, password: e.target.value}))}
                    placeholder="Min 8 characters"
                    required
                    data-testid="team-add-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="md:col-span-3 flex gap-2 justify-end">
                <Button type="button" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button type="submit" className="btn-primary gap-2" disabled={adding} data-testid="team-add-submit">
                  {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {adding ? 'Adding...' : 'Add Member'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Members List */}
        {members.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-2">No team members yet</p>
            <p className="text-sm text-slate-400">Add team members to collaborate on your compliance documents.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {members.map(member => (
              <div key={member.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`team-member-${member.id}`}>
                {/* Member header */}
                <div className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-indigo-600 font-bold text-sm">{member.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      {editTarget?.id === member.id ? (
                        <form onSubmit={handleEdit} className="flex items-center gap-2">
                          <Input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} className="h-8 w-40" data-testid="team-edit-name" />
                          <Input value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} className="h-8 w-52" data-testid="team-edit-email" />
                          <Button type="submit" size="sm" className="h-8 gap-1" disabled={editSaving} data-testid="team-edit-save">
                            {editSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setEditTarget(null)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </form>
                      ) : (
                        <>
                          <p className="font-semibold text-slate-900">{member.name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1"><Mail className="h-3 w-3" /> {member.email}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => { setEditTarget(member); setEditForm({ name: member.name, email: member.email }); }}
                      className="text-slate-500"
                      data-testid={`team-edit-${member.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(member)} className="text-red-500" data-testid={`team-delete-${member.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Access toggles */}
                <div className="border-t border-slate-100 bg-slate-50/50 p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5" /> Document Access
                  </p>
                  {submissions.length === 0 ? (
                    <p className="text-xs text-slate-400">No submissions to assign. Start a template first.</p>
                  ) : (
                    <div className="space-y-2">
                      {submissions.map(sub => {
                        const hasAccess = (member.submission_access || []).includes(sub.id);
                        return (
                          <div key={sub.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <span className="text-sm text-slate-700">{sub.template_name}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${sub.is_completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {sub.is_completed ? 'Completed' : 'In Progress'}
                              </span>
                            </div>
                            <Switch
                              checked={hasAccess}
                              onCheckedChange={() => toggleAccess(member.id, sub.id, hasAccess)}
                              disabled={accessSaving}
                              data-testid={`team-access-${member.id}-${sub.id}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {deleteTarget?.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove their access to all assigned documents. They will no longer be able to log in.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={deleting} data-testid="confirm-delete-member">
                {deleting ? 'Removing...' : 'Remove Member'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ClientLayout>
  );
}
